import React, { useMemo } from 'react';
import { ShieldCheck, AlertTriangle, Lock } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_OBJECT } from '../../store/stableEmpties';
import { TAX_OPTIMIZATIONS } from '../../data/taxes';
import { projectAnnualProfit } from '../../utils/projectAnnualProfit';
import { formatMoney } from '../../utils/format';

/**
 * Audit fiscal — débloqué par Sophie Lambert (Comptable).
 * Liste les optimisations fiscales NON activées que l'apprenant pourrait
 * encore acheter à ce stade de la partie. Pour chaque opportunité on
 * affiche un libellé + un gain potentiel (% IS ou crédit / chauffeur).
 *
 * Conçu pour ne JAMAIS révéler une optimisation hors-périmètre (mauvais
 * statut juridique, prérequis non rempli) — on filtre sur `requires`.
 */
export default function AuditFiscalPanel() {
  const hasSupportUnlock = useGameStore(s => s.hasSupportUnlock);
  const activeOptimizations = useGameStore(s => s.activeOptimizations || EMPTY_OBJECT);
  const driversCount = useGameStore(s => (s.drivers || []).length);
  const annualProfit = useGameStore(s => s.annualProfit || 0);
  const month = useGameStore(s => s.month || 1);
  const isUnlocked = typeof hasSupportUnlock === 'function' && hasSupportUnlock('audit_fiscal');

  const opportunities = useMemo(() => {
    const list = [];
    Object.entries(TAX_OPTIMIZATIONS).forEach(([id, opt]) => {
      if (activeOptimizations[id]) return; // déjà actif → pas une opportunité.
      let gainHint = '';
      if (opt.isReduction) {
        const estProfit = projectAnnualProfit({ annualProfit, month }).annualProfit;
        const gain = Math.round(estProfit * opt.isReduction * 0.20); // approx 20% effective rate
        gainHint = gain > 0 ? `≈ ${formatMoney(gain)} d'IS économisé sur l'année` : `${Math.round(opt.isReduction * 100)} % d'IS en moins`;
      } else if (opt.creditPerDriver) {
        const gain = driversCount * opt.creditPerDriver;
        gainHint = gain > 0
          ? `≈ ${formatMoney(gain)} de crédit d'impôt (${driversCount} chauffeur${driversCount > 1 ? 's' : ''})`
          : `${formatMoney(opt.creditPerDriver)} / chauffeur formé`;
      } else if (opt.suramortissement) {
        gainHint = `Suramortissement de ${Math.round((opt.suramortissement - 1) * 100)} % sur les véhicules verts`;
      } else {
        gainHint = opt.description || 'Optimisation fiscale disponible';
      }
      list.push({ id, name: opt.name || id, hint: gainHint, cost: opt.cost || 0 });
    });
    return list;
  }, [activeOptimizations, annualProfit, month, driversCount]);

  if (!isUnlocked) {
    return (
      <div
        className="bg-ink-800 rounded-lg border border-amber-700/40 p-4"
        data-testid="audit-fiscal-locked"
      >
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-amber-500/15">
            <Lock className="w-4 h-4 text-amber-300" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-200">Audit fiscal — Comptable requis</p>
            <p className="text-xs text-slate-400 mt-1 leading-relaxed">
              Recrutez un·e <strong className="text-slate-200">Comptable</strong> (Sophie Lambert) pour activer un
              audit fiscal automatique : la liste des optimisations que vous n'avez pas encore activées,
              avec leur gain potentiel chiffré.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ink-800 rounded-lg border border-emerald-700/30 p-4" data-testid="audit-fiscal-panel">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-emerald-300" />
        <p className="text-sm font-semibold text-slate-200">Audit fiscal — opportunités</p>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
          Comptable
        </span>
      </div>

      {opportunities.length === 0 ? (
        <p className="text-xs text-slate-500 text-center py-3" data-testid="audit-fiscal-empty">
          ✅ Toutes les optimisations disponibles sont activées. Bon travail.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="audit-fiscal-list">
          {opportunities.map(o => (
            <li
              key={o.id}
              className="flex items-start justify-between gap-3 p-2 rounded bg-ink-700/40 border border-ink-600"
              data-testid={`audit-fiscal-${o.id}`}
            >
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-300 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-200 truncate">{o.name}</p>
                  <p className="text-[10px] text-slate-400">{o.hint}</p>
                </div>
              </div>
              {o.cost > 0 && (
                <span className="text-[10px] text-slate-500 flex-shrink-0">
                  {formatMoney(o.cost)} à payer
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
